/**
 * This code was AUTOGENERATED using the Codama library.
 * Please DO NOT EDIT THIS FILE, instead use visitors
 * to add features, then rerun Codama to update it.
 *
 * @see https://github.com/codama-idl/codama
 */

import {
  combineCodec,
  fixDecoderSize,
  fixEncoderSize,
  getAddressDecoder,
  getAddressEncoder,
  getBytesDecoder,
  getBytesEncoder,
  getProgramDerivedAddress,
  getStructDecoder,
  getStructEncoder,
  SOLANA_ERROR__PROGRAM_CLIENTS__INSUFFICIENT_ACCOUNT_METAS,
  SolanaError,
  transformEncoder,
  type AccountMeta,
  type AccountSignerMeta,
  type Address,
  type FixedSizeCodec,
  type FixedSizeDecoder,
  type FixedSizeEncoder,
  type Instruction,
  type InstructionWithAccounts,
  type InstructionWithData,
  type ReadonlyAccount,
  type ReadonlySignerAccount,
  type ReadonlyUint8Array,
  type TransactionSigner,
  type WritableAccount,
} from '@solana/kit';
import {
  getAccountMetaFactory,
  getAddressFromResolvedInstructionAccount,
  type ResolvedInstructionAccount,
} from '@solana/program-client-core';
import { DOPPLER_REHYPE_ROUTER_V1_PROGRAM_ADDRESS } from '../programs';

export const REPLACE_BENEFICIARY_DISCRIMINATOR = new Uint8Array([
  255, 125, 195, 81, 143, 176, 242, 22,
]);

export function getReplaceBeneficiaryDiscriminatorBytes() {
  return fixEncoderSize(getBytesEncoder(), 8).encode(
    REPLACE_BENEFICIARY_DISCRIMINATOR,
  );
}

export type ReplaceBeneficiaryInstruction<
  TProgram extends string = typeof DOPPLER_REHYPE_ROUTER_V1_PROGRAM_ADDRESS,
  TAccountCurrentBeneficiary extends string | AccountMeta<string> = string,
  TAccountBaseMint extends string | AccountMeta<string> = string,
  TAccountRehypeState extends string | AccountMeta<string> = string,
  TRemainingAccounts extends readonly AccountMeta<string>[] = [],
> = Instruction<TProgram> &
  InstructionWithData<ReadonlyUint8Array> &
  InstructionWithAccounts<
    [
      TAccountCurrentBeneficiary extends string
        ? ReadonlySignerAccount<TAccountCurrentBeneficiary> &
            AccountSignerMeta<TAccountCurrentBeneficiary>
        : TAccountCurrentBeneficiary,
      TAccountBaseMint extends string
        ? ReadonlyAccount<TAccountBaseMint>
        : TAccountBaseMint,
      TAccountRehypeState extends string
        ? WritableAccount<TAccountRehypeState>
        : TAccountRehypeState,
      ...TRemainingAccounts,
    ]
  >;

export type ReplaceBeneficiaryInstructionData = {
  discriminator: ReadonlyUint8Array;
  newWallet: Address;
};

export type ReplaceBeneficiaryInstructionDataArgs = { newWallet: Address };

export function getReplaceBeneficiaryInstructionDataEncoder(): FixedSizeEncoder<ReplaceBeneficiaryInstructionDataArgs> {
  return transformEncoder(
    getStructEncoder([
      ['discriminator', fixEncoderSize(getBytesEncoder(), 8)],
      ['newWallet', getAddressEncoder()],
    ]),
    (value) => ({ ...value, discriminator: REPLACE_BENEFICIARY_DISCRIMINATOR }),
  );
}

export function getReplaceBeneficiaryInstructionDataDecoder(): FixedSizeDecoder<ReplaceBeneficiaryInstructionData> {
  return getStructDecoder([
    ['discriminator', fixDecoderSize(getBytesDecoder(), 8)],
    ['newWallet', getAddressDecoder()],
  ]);
}

export function getReplaceBeneficiaryInstructionDataCodec(): FixedSizeCodec<
  ReplaceBeneficiaryInstructionDataArgs,
  ReplaceBeneficiaryInstructionData
> {
  return combineCodec(
    getReplaceBeneficiaryInstructionDataEncoder(),
    getReplaceBeneficiaryInstructionDataDecoder(),
  );
}

export type ReplaceBeneficiaryAsyncInput<
  TAccountCurrentBeneficiary extends string = string,
  TAccountBaseMint extends string = string,
  TAccountRehypeState extends string = string,
> = {
  currentBeneficiary: TransactionSigner<TAccountCurrentBeneficiary>;
  /** Used to derive and validate the canonical Rehype state PDA. */
  baseMint: Address<TAccountBaseMint>;
  rehypeState?: Address<TAccountRehypeState>;
  newWallet: ReplaceBeneficiaryInstructionDataArgs['newWallet'];
};

export async function getReplaceBeneficiaryInstructionAsync<
  TAccountCurrentBeneficiary extends string,
  TAccountBaseMint extends string,
  TAccountRehypeState extends string,
  TProgramAddress extends Address =
    typeof DOPPLER_REHYPE_ROUTER_V1_PROGRAM_ADDRESS,
>(
  input: ReplaceBeneficiaryAsyncInput<
    TAccountCurrentBeneficiary,
    TAccountBaseMint,
    TAccountRehypeState
  >,
  config?: { programAddress?: TProgramAddress },
): Promise<
  ReplaceBeneficiaryInstruction<
    TProgramAddress,
    TAccountCurrentBeneficiary,
    TAccountBaseMint,
    TAccountRehypeState
  >
> {
  // Program address.
  const programAddress =
    config?.programAddress ?? DOPPLER_REHYPE_ROUTER_V1_PROGRAM_ADDRESS;

  // Original accounts.
  const originalAccounts = {
    currentBeneficiary: {
      value: input.currentBeneficiary ?? null,
      isWritable: false,
    },
    baseMint: { value: input.baseMint ?? null, isWritable: false },
    rehypeState: { value: input.rehypeState ?? null, isWritable: true },
  };
  const accounts = originalAccounts as Record<
    keyof typeof originalAccounts,
    ResolvedInstructionAccount
  >;

  // Original args.
  const args = { ...input };

  // Resolve default values.
  if (!accounts.rehypeState.value) {
    accounts.rehypeState.value = await getProgramDerivedAddress({
      programAddress,
      seeds: [
        getBytesEncoder().encode(
          new Uint8Array([
            114, 101, 104, 121, 112, 101, 95, 115, 116, 97, 116, 101,
          ]),
        ),
        getAddressEncoder().encode(
          getAddressFromResolvedInstructionAccount(
            'baseMint',
            accounts.baseMint.value,
          ),
        ),
      ],
    });
  }

  const getAccountMeta = getAccountMetaFactory(programAddress, 'programId');
  return Object.freeze({
    accounts: [
      getAccountMeta('currentBeneficiary', accounts.currentBeneficiary),
      getAccountMeta('baseMint', accounts.baseMint),
      getAccountMeta('rehypeState', accounts.rehypeState),
    ],
    data: getReplaceBeneficiaryInstructionDataEncoder().encode(
      args as ReplaceBeneficiaryInstructionDataArgs,
    ),
    programAddress,
  } as ReplaceBeneficiaryInstruction<
    TProgramAddress,
    TAccountCurrentBeneficiary,
    TAccountBaseMint,
    TAccountRehypeState
  >);
}

export type ReplaceBeneficiaryInput<
  TAccountCurrentBeneficiary extends string = string,
  TAccountBaseMint extends string = string,
  TAccountRehypeState extends string = string,
> = {
  currentBeneficiary: TransactionSigner<TAccountCurrentBeneficiary>;
  /** Used to derive and validate the canonical Rehype state PDA. */
  baseMint: Address<TAccountBaseMint>;
  rehypeState: Address<TAccountRehypeState>;
  newWallet: ReplaceBeneficiaryInstructionDataArgs['newWallet'];
};

export function getReplaceBeneficiaryInstruction<
  TAccountCurrentBeneficiary extends string,
  TAccountBaseMint extends string,
  TAccountRehypeState extends string,
  TProgramAddress extends Address =
    typeof DOPPLER_REHYPE_ROUTER_V1_PROGRAM_ADDRESS,
>(
  input: ReplaceBeneficiaryInput<
    TAccountCurrentBeneficiary,
    TAccountBaseMint,
    TAccountRehypeState
  >,
  config?: { programAddress?: TProgramAddress },
): ReplaceBeneficiaryInstruction<
  TProgramAddress,
  TAccountCurrentBeneficiary,
  TAccountBaseMint,
  TAccountRehypeState
> {
  // Program address.
  const programAddress =
    config?.programAddress ?? DOPPLER_REHYPE_ROUTER_V1_PROGRAM_ADDRESS;

  // Original accounts.
  const originalAccounts = {
    currentBeneficiary: {
      value: input.currentBeneficiary ?? null,
      isWritable: false,
    },
    baseMint: { value: input.baseMint ?? null, isWritable: false },
    rehypeState: { value: input.rehypeState ?? null, isWritable: true },
  };
  const accounts = originalAccounts as Record<
    keyof typeof originalAccounts,
    ResolvedInstructionAccount
  >;

  // Original args.
  const args = { ...input };

  const getAccountMeta = getAccountMetaFactory(programAddress, 'programId');
  return Object.freeze({
    accounts: [
      getAccountMeta('currentBeneficiary', accounts.currentBeneficiary),
      getAccountMeta('baseMint', accounts.baseMint),
      getAccountMeta('rehypeState', accounts.rehypeState),
    ],
    data: getReplaceBeneficiaryInstructionDataEncoder().encode(
      args as ReplaceBeneficiaryInstructionDataArgs,
    ),
    programAddress,
  } as ReplaceBeneficiaryInstruction<
    TProgramAddress,
    TAccountCurrentBeneficiary,
    TAccountBaseMint,
    TAccountRehypeState
  >);
}

export type ParsedReplaceBeneficiaryInstruction<
  TProgram extends string = typeof DOPPLER_REHYPE_ROUTER_V1_PROGRAM_ADDRESS,
  TAccountMetas extends readonly AccountMeta[] = readonly AccountMeta[],
> = {
  programAddress: Address<TProgram>;
  accounts: {
    currentBeneficiary: TAccountMetas[0];
    /** Used to derive and validate the canonical Rehype state PDA. */
    baseMint: TAccountMetas[1];
    rehypeState: TAccountMetas[2];
  };
  data: ReplaceBeneficiaryInstructionData;
};

export function parseReplaceBeneficiaryInstruction<
  TProgram extends string,
  TAccountMetas extends readonly AccountMeta[],
>(
  instruction: Instruction<TProgram> &
    InstructionWithAccounts<TAccountMetas> &
    InstructionWithData<ReadonlyUint8Array>,
): ParsedReplaceBeneficiaryInstruction<TProgram, TAccountMetas> {
  if (instruction.accounts.length < 3) {
    throw new SolanaError(
      SOLANA_ERROR__PROGRAM_CLIENTS__INSUFFICIENT_ACCOUNT_METAS,
      {
        actualAccountMetas: instruction.accounts.length,
        expectedAccountMetas: 3,
      },
    );
  }
  let accountIndex = 0;
  const getNextAccount = () => {
    const accountMeta = (instruction.accounts as TAccountMetas)[accountIndex]!;
    accountIndex += 1;
    return accountMeta;
  };
  return {
    programAddress: instruction.programAddress,
    accounts: {
      currentBeneficiary: getNextAccount(),
      baseMint: getNextAccount(),
      rehypeState: getNextAccount(),
    },
    data: getReplaceBeneficiaryInstructionDataDecoder().decode(
      instruction.data,
    ),
  };
}
